import React from "react";
import { TAP_LINK_STYLE } from "./tapTarget";

/**
 * SecondaryAction — issue #750 § 5.6. `variant="boxed"` is the bordered
 * secondary button; `variant="link"` is bare navy text. `as` lets a caller
 * render it as `Link` (react-router) when the action navigates rather than
 * handles a click.
 *
 * `variant="navy-outline"` (issue #757 review round 3, F2) — a full-width
 * button, white background, navy text and a 1px navy border:
 * `FirstGround.dc.html:103`'s own drawn "Save with this town", which
 * `boxed`'s defaults (`--nw-line` border, `--nw-ink-muted` text,
 * content-sized width) do not match. A new option, not a restyle of
 * `boxed`'s own default — every existing `boxed`/`link` caller keeps its
 * current appearance unchanged.
 */
export const SecondaryAction = React.forwardRef(function SecondaryAction(
  { variant = "boxed", children, className = "", "data-testid": testId, style, as: Comp = "button", ...rest },
  ref
) {
  const typeProp = Comp === "button" ? { type: rest.type || "button" } : {};

  if (variant === "navy-outline") {
    // D108 (merge-minds-nz/cricket#1027, closes #840 I1/I4) — a disabled
    // navy-outline (`LogMatchAddGroundPanel.jsx`'s "Save with this town")
    // keeps its white fill and takes a muted border/label and a
    // not-allowed cursor; opacity is untouched (distinct from the gold
    // `ActionButton`'s own 0.6-opacity disabled look, left alone). Live —
    // `disabled` false/absent — stays byte-identical to before this fix,
    // which every other navy-outline caller (`DevelopmentPlan.jsx`'s "New
    // goal", `Reflections.jsx`'s "Cancel") depends on: neither passes
    // `disabled`, so neither is affected.
    //
    // BUTTON FORM ONLY (#1053 review round 2, F3 ruling) — an earlier
    // version of this fix also guarded `onClick` for `as={Link}`, on the
    // theory that `aria-disabled` plus a click guard could stand in for a
    // real `disabled` attribute there. Dropped: no caller passes
    // `as={Link}` with `disabled` on this variant, there is no `disabled`
    // DOM attribute for an anchor to begin with (`<a disabled>` is
    // invalid HTML and does nothing), and the guard only catches the
    // synthetic `onClick` — a middle-click or ctrl/cmd-click on a real
    // `<a href>` still opens it natively, so the guard would have been a
    // false promise of "disabled" rather than a real one. `disabled` is
    // therefore only ever true for the native `<button>` form; `as={Link}`
    // renders exactly as it did before this fix, with `disabled` passed
    // through inertly like any other prop this component doesn't
    // recognise.
    const isButtonForm = Comp === "button";
    const disabled = isButtonForm && Boolean(rest.disabled);
    const { onClick, ...restWithoutOnClick } = rest;
    return (
      <Comp
        ref={ref}
        data-testid={testId}
        aria-disabled={disabled || undefined}
        className={`flex items-center justify-center w-full ${className}`}
        style={{
          minHeight: "var(--nw-size-action)",
          padding: "0 18px",
          background: "var(--nw-surface)",
          border: disabled ? "1px solid var(--nw-line-strong)" : "1px solid var(--nw-navy)",
          borderRadius: "var(--nw-radius-control)",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontWeight: 600,
          fontSize: 15,
          color: disabled ? "var(--nw-ink-faint)" : "var(--nw-navy)",
          cursor: disabled ? "not-allowed" : "pointer",
          ...style,
        }}
        {...typeProp}
        {...restWithoutOnClick}
        onClick={
          isButtonForm
            ? (e) => {
                // The native `disabled` attribute already blocks a real
                // `<button>`'s click before React's synthetic `onClick`
                // ever fires; this guard only covers the (currently
                // unreached) case of a future caller setting `disabled`
                // without the native attribute doing its job for some
                // reason. Scoped to the button form — see the comment
                // above for why the Link form gets none of this.
                if (disabled) {
                  e.preventDefault();
                  return;
                }
                onClick?.(e);
              }
            : onClick
        }
      >
        {children}
      </Comp>
    );
  }

  if (variant === "link") {
    return (
      <Comp
        ref={ref}
        data-testid={testId}
        className={className}
        style={{
          color: "var(--nw-navy)",
          fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
          fontWeight: 600,
          fontSize: 14,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          // #1163 (D221 G4): the link variant is at least 44px to tap; the
          // drawn text does not move (`tapTarget.js`).
          ...TAP_LINK_STYLE,
          ...style,
        }}
        {...typeProp}
        {...rest}
      >
        {children}
      </Comp>
    );
  }

  return (
    <Comp
      ref={ref}
      data-testid={testId}
      className={`flex items-center justify-center ${className}`}
      style={{
        minHeight: "var(--nw-size-action)",
        padding: "0 18px",
        background: "var(--nw-surface)",
        border: "1px solid var(--nw-line)",
        borderRadius: "var(--nw-radius-control)",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        fontWeight: 600,
        fontSize: 15,
        color: "var(--nw-ink-muted)",
        cursor: "pointer",
        ...style,
      }}
      {...typeProp}
      {...rest}
    >
      {children}
    </Comp>
  );
});

export default SecondaryAction;
